/**
 * Gate R2 Phase D3 / E — Dashboard partition from relationship facts.
 *
 * Canonical terminology:
 *   Company Representative accessing the Company Portal → Company Dashboard
 *   Partner Representative accessing the Partner Portal → Partner Dashboard
 *   Vendor Representative → Vendor Dashboard (portal in E2)
 *
 * Account role alone must not select a company-side assigned-work experience.
 * These partitions must never surface org-wide admin, security, or platform
 * controls (those stay on Analytics / Control Plane paths).
 */
import type { User } from '@/types';
import { hasScopedAccessLevel } from '@/types';
import { resolveIdentityRoles } from './identityRoles';
import { getActiveScopedRelationships } from './scopedRelationshipResolver';
import { isAwaitingAssignment } from './awaitingAssignment';
import { isFocusedAssignmentParticipant } from './workspaceSurfaceEntry';
import {
  COMPANY_DASHBOARD_LABEL,
  companyPortalHref,
  resolveCompanyPortalEntry,
} from './companyPortal';
import { customerRouteHref } from '@/lib/navigation/canonicalRoutes';
import type { NavigationContextOption } from '@/lib/navigation/navigationContexts';

/** Re-export canonical dashboard labels for selector / evidence consumers. */
export { COMPANY_DASHBOARD_LABEL };

export const OPERATING_DASHBOARD_LABEL = 'Operating Dashboard';
export const ANALYTICS_DASHBOARD_LABEL = 'Analytics Dashboard';
export const WELCOME_DASHBOARD_LABEL = 'Welcome';

export type DashboardPartitionKind =
  | 'platform'
  | 'company'
  | 'partner'
  | 'vendor'
  | 'investor'
  | 'focused'
  | 'analytics'
  | 'operating'
  | 'welcome'
  | 'unknown';

export interface DashboardPartitionResult {
  readonly kind: DashboardPartitionKind;
  /** Product label for the selected partition (UI / evidence). */
  readonly label: string;
  /**
   * Destination when the selector should Navigate instead of mount a widget.
   * Relationship-assigned dashboards mount their dedicated widgets, so only
   * partitions with a separate route shell set this value.
   */
  readonly href: string | null;
}

export interface ResolveDashboardPartitionOptions {
  /** Validated workspace id when known (ambiguous company portal entry). */
  readonly workspaceId?: string | null;
  /** Explicit presentation context chosen by the user. */
  readonly navigationContext?: NavigationContextOption | null;
}

function hasActiveRelationship(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
  type: 'company_representative' | 'partner_representative' | 'vendor_representative',
): boolean {
  return getActiveScopedRelationships(user).some(
    (relationship) =>
      relationship.type === type
      && hasScopedAccessLevel(relationship.accessLevel, 'view'),
  );
}

/**
 * Resolve which dashboard partition applies for the authenticated user.
 *
 * Precedence (relationship facts before org-admin analytics):
 *   1. platform role → Control Plane
 *   2. company_representative → Company Dashboard (/companies/:id/dashboard)
 *   3. partner_representative → Partner assigned-work dashboard
 *   4. vendor_representative → Vendor assigned-work dashboard
 *   5. organization_owner / organization_admin → Analytics
 *   6. known member / other → Operating (PM-style)
 *   7. unknown → fail-soft operating fallback
 */
export function resolveDashboardPartition(
  user: User | null | undefined,
  options: ResolveDashboardPartitionOptions = {},
): DashboardPartitionResult {
  if (!user) {
    return { kind: 'unknown', label: OPERATING_DASHBOARD_LABEL, href: null };
  }

  const { accountRole, platformRole } = resolveIdentityRoles(user);
  const selectedContext = options.navigationContext;

  if (platformRole) {
    return { kind: 'platform', label: 'Control Plane', href: '/platform' };
  }

  if (selectedContext?.kind === 'account') {
    if (accountRole && accountRole !== 'organization_member') {
      return { kind: 'analytics', label: ANALYTICS_DASHBOARD_LABEL, href: null };
    }
    if (accountRole === 'organization_member') {
      if (isFocusedAssignmentParticipant(user)) {
        return { kind: 'focused', label: 'Assigned Reviews', href: null };
      }
      return isAwaitingAssignment(user)
        ? { kind: 'welcome', label: WELCOME_DASHBOARD_LABEL, href: null }
        : { kind: 'operating', label: OPERATING_DASHBOARD_LABEL, href: null };
    }
  }

  if (selectedContext?.kind === 'company') {
    return {
      kind: 'company',
      label: COMPANY_DASHBOARD_LABEL,
      href: companyPortalHref(selectedContext.resourceId, 'dashboard'),
    };
  }
  if (selectedContext?.kind === 'investor') {
    return { kind: 'investor', label: 'Investor Portal', href: '/investor/dashboard' };
  }
  if (selectedContext?.kind === 'partner') {
    return { kind: 'partner', label: 'Partner Assigned Work', href: null };
  }
  if (selectedContext?.kind === 'vendor') {
    return { kind: 'vendor', label: 'Vendor Assigned Work', href: null };
  }

  if (hasActiveRelationship(user, 'company_representative')) {
    const entry = resolveCompanyPortalEntry(user);
    if (entry.kind === 'one') {
      return {
        kind: 'company',
        label: COMPANY_DASHBOARD_LABEL,
        href: companyPortalHref(entry.companyId, 'dashboard'),
      };
    }
    if (entry.kind === 'many') {
      return { kind: 'company', label: COMPANY_DASHBOARD_LABEL, href: '/companies/select' };
    }
    return { kind: 'company', label: COMPANY_DASHBOARD_LABEL, href: '/unauthorized' };
  }

  if (hasActiveRelationship(user, 'partner_representative')) {
    return {
      kind: 'partner',
      label: 'Partner Assigned Work',
      href: null,
    };
  }

  if (hasActiveRelationship(user, 'vendor_representative')) {
    return {
      kind: 'vendor',
      label: 'Vendor Assigned Work',
      href: null,
    };
  }

  if (accountRole && accountRole !== 'organization_member') {
    return {
      kind: 'analytics',
      label: ANALYTICS_DASHBOARD_LABEL,
      href: null,
    };
  }

  if (accountRole === 'organization_member') {
    if (isFocusedAssignmentParticipant(user)) {
      return {
        kind: 'focused',
        label: 'Assigned Reviews',
        href: null,
      };
    }
    if (isAwaitingAssignment(user)) {
      return {
        kind: 'welcome',
        label: WELCOME_DASHBOARD_LABEL,
        href: null,
      };
    }
    return {
      kind: 'operating',
      label: OPERATING_DASHBOARD_LABEL,
      href: null,
    };
  }

  return { kind: 'unknown', label: OPERATING_DASHBOARD_LABEL, href: null };
}

/** True when the partition must not mount org admin / analytics / platform widgets. */
export function isRelationshipScopedDashboard(
  kind: DashboardPartitionKind,
): boolean {
  return kind === 'company' || kind === 'partner' || kind === 'vendor' || kind === 'investor';
}

/**
 * Portal-scoped nav href for relationship partitions.
 *
 * When the user is a Company / Partner / Vendor Representative with a single
 * represented entity, primary Work/Operations links must stay inside that
 * portal — not bounce through flat `/dashboard` or `/projects` (operating
 * workspace) surfaces. Returns null when no single portal scope applies
 * (operating / analytics / ambiguous / none).
 */
export type RelationshipPortalNavSurface =
  | 'dashboard'
  | 'projects'
  | 'tasks'
  | 'assets'
  | 'reports'
  | 'decisions'
  | 'participants'
  | 'activity';

export function resolveRelationshipPortalNavHref(
  user: User | null | undefined,
  surface: RelationshipPortalNavSurface,
  workspaceId?: string | null,
  navigationContext?: NavigationContextOption | null,
): string | null {
  if (!user) return null;

  if (navigationContext?.kind === 'account') return null;

  if (navigationContext?.kind === 'company') {
    return companyPortalHref(navigationContext.resourceId, surface);
  }

  if (navigationContext?.kind === 'partner') {
    if (surface === 'decisions' || surface === 'participants') return null;
    const target = surface === 'dashboard'
      ? 'dashboard'
      : surface === 'tasks'
        ? 'tasks'
        : surface === 'assets'
          ? 'assets'
          : surface === 'reports'
            ? 'reports'
            : 'projects';
    return workspaceId
      ? customerRouteHref(workspaceId, target, { search: { context: navigationContext.key } })
      : '/workspaces/select';
  }

  if (navigationContext?.kind === 'vendor') {
    if (surface === 'decisions' || surface === 'participants' || surface === 'reports') return null;
    const target = surface === 'dashboard'
      ? 'dashboard'
      : surface === 'tasks'
        ? 'tasks'
        : surface === 'assets'
          ? 'assets'
          : 'projects';
    return workspaceId
      ? customerRouteHref(workspaceId, target, { search: { context: navigationContext.key } })
      : '/workspaces/select';
  }

  if (navigationContext?.kind === 'investor') return null;

  if (hasActiveRelationship(user, 'company_representative')) {
    const entry = resolveCompanyPortalEntry(user);
    if (entry.kind === 'many') return '/companies/select';
    if (entry.kind !== 'one') return null;
    return companyPortalHref(entry.companyId, surface);
  }

  if (hasActiveRelationship(user, 'partner_representative')) {
    if (surface === 'decisions' || surface === 'participants') return null;
    const target = surface === 'dashboard'
      ? 'dashboard'
      : surface === 'tasks'
        ? 'tasks'
      : surface === 'assets'
          ? 'assets'
          : surface === 'reports'
            ? 'reports'
          : 'projects';
    return workspaceId
      ? customerRouteHref(workspaceId, target)
      : '/workspaces/select';
  }

  if (hasActiveRelationship(user, 'vendor_representative')) {
    if (surface === 'decisions' || surface === 'participants' || surface === 'reports') {
      return null;
    }
    const target = surface === 'dashboard'
      ? 'dashboard'
      : surface === 'tasks'
        ? 'tasks'
        : surface === 'assets'
          ? 'assets'
          : 'projects';
    return workspaceId
      ? customerRouteHref(workspaceId, target)
      : '/workspaces/select';
  }

  return null;
}
